import { supabase } from '../supabaseClient';

/**
 * Postęp zlecenia (audyt pamięci 22.10): RPC `order_progress(p_order_id)` zamiast widoku `dispatcher_health`.
 * RPC liczy postęp z `engine_products` (per produkt), widzi tylko zlecenia zalogowanego (jak RLS `orders`) i zwraca
 * te same nazwy pól co widok + `products` = { pending, running, done, failed } i `last_activity_at`.
 * Fallback: gdy RPC zwróci błąd (np. prod jeszcze bez funkcji) — `dispatcher_health` jak dotąd.
 */
let rpcMissing = false; // funkcji nie ma w bazie (prod) — nie wołamy jej w kółko

export function isMissingFunction(error) {
  const code = String(error?.code || '');
  return code === 'PGRST202' || code === '42883' || /could not find the function|does not exist/i.test(error?.message || '');
}

/** { data, source: 'rpc' | 'view' } albo { data: null } — `data` ma kształt wiersza `dispatcher_health`. */
export async function fetchOrderProgress(orderId) {
  if (!orderId) return { data: null };
  if (!rpcMissing) {
    const { data, error } = await supabase.rpc('order_progress', { p_order_id: String(orderId) });
    if (!error) return { data: data || null, source: 'rpc' };
    if (isMissingFunction(error)) rpcMissing = true;
    console.warn('[order_progress] fallback na dispatcher_health:', error.message || error);
  }
  const { data, error } = await supabase.from('dispatcher_health').select('*').eq('order_id', orderId).maybeSingle();
  if (error) return { data: null, error };
  return { data: data || null, source: 'view' };
}

/**
 * Aktywne zlecenie dla Live Dashboard: najpierw `processing`, potem `queued`, FIFO po created_at.
 * Ścieżka RPC: jedno małe zapytanie do `orders` + `order_progress`. Fallback: widok jak dotąd.
 */
export async function fetchActiveOrderProgress() {
  if (!rpcMissing) {
    const { data: o, error } = await supabase.from('orders').select('id, status, created_at')
      .in('status', ['processing', 'queued']).order('status', { ascending: true }).order('created_at', { ascending: true }).limit(1);
    if (!error) {
      if (!o?.length) return { data: null, source: 'rpc' };
      const res = await fetchOrderProgress(o[0].id);
      if (res.source === 'rpc' || res.data) return res;
    }
  }
  const { data } = await supabase.from('dispatcher_health').select('*').in('order_status', ['processing', 'queued'])
    .order('order_status', { ascending: true }).order('created_at', { ascending: true }).limit(1);
  return { data: data?.[0] ?? null, source: 'view' };
}

/**
 * Liczniki do pierścienia / pigułek. Z RPC — PER PRODUKT (`products` z engine_products; batch na dev = 400 produktów,
 * więc liczenie po batchach nic nie mówi). Z widoku — po batchach jak dotąd.
 */
export function progressCounts(p) {
  if (!p) return null;
  const productsTotal = p.product_count || 0;
  const productsDone = p.results_saved || 0;
  const pr = p.products;
  const prSum = pr ? ['pending', 'running', 'done', 'failed'].reduce((s, k) => s + (Number(pr[k]) || 0), 0) : 0;
  if (pr && prSum > 0) {
    return {
      unit: 'product', total: Math.max(productsTotal, prSum), done: Number(pr.done) || 0, processing: Number(pr.running) || 0,
      pending: Number(pr.pending) || 0, failed: Number(pr.failed) || 0, productsDone, productsTotal: productsTotal || prSum,
    };
  }
  return {
    unit: 'batch', total: p.total_batches || 1, done: p.done || 0, processing: p.processing || 0,
    pending: p.pending || 0, failed: p.failed || 0, productsDone, productsTotal,
  };
}

/**
 * Przepustowość z kolejnych odczytów `results_saved` (= produkty zakończone w engine_products; tabela nie jest czytelna
 * dla zalogowanego, więc zamiast `updated_at` per wiersz liczymy przyrost między odczytami). Okno `windowMin` minut.
 */
export function createThroughputSampler(windowMin = 30) {
  let orderId = null;
  let samples = []; // { t, v }
  return {
    push(id, value, t = Date.now()) {
      if (id !== orderId) { orderId = id; samples = []; }
      const v = Number(value) || 0;
      const last = samples[samples.length - 1];
      if (last && v < last.v) samples = []; // reset licznika (np. ponowny bieg) — zaczynamy od nowa
      samples.push({ t, v });
      const cut = t - windowMin * 60_000;
      while (samples.length > 2 && samples[1].t < cut) samples.shift();
    },
    reset() { orderId = null; samples = []; },
    /** produkty / min z ostatnich `min` minut (null = za mało odczytów) */
    perMin(min = 5, now = Date.now()) {
      if (samples.length < 2) return null;
      const from = now - min * 60_000;
      const first = samples.find(s => s.t >= from) || samples[0];
      const base = samples.indexOf(first) > 0 ? samples[samples.indexOf(first) - 1] : first;
      const last = samples[samples.length - 1];
      const dt = (last.t - base.t) / 60_000;
      return dt > 0 ? (last.v - base.v) / dt : null;
    },
    /** [{ time, value }] — przyrost na minutę, `windowMin` kubełków */
    buckets(now = Date.now()) {
      const map = new Map();
      for (let i = windowMin - 1; i >= 0; i--) {
        const m = new Date(now - i * 60_000); m.setSeconds(0, 0);
        map.set(m.toISOString(), 0);
      }
      for (let i = 1; i < samples.length; i++) {
        const d = samples[i].v - samples[i - 1].v;
        if (d <= 0) continue;
        const m = new Date(samples[i].t); m.setSeconds(0, 0);
        const k = m.toISOString();
        if (map.has(k)) map.set(k, map.get(k) + d);
      }
      return Array.from(map.entries()).map(([time, value]) => ({ time, value }));
    },
  };
}

/**
 * Poprawka v13 (24.10, C): łuki pierścienia Live Dashboard (długości w jednostkach obwodu `C`).
 * done = pełny niebieski (produkty z wynikiem), processing = półprzezroczysty — WYŁĄCZNIE z liczników per produkt
 * (`countsUnit: 'product'`, engine_products.running), failed = czerwony; reszta (pending) = szary tor.
 * Liczniki po batchach (`'batch'`) nie dają łuku „w toku”: przy batch_size 400 jeden przyjęty batch = całe zlecenie,
 * więc na starcie całe koło było półprzezroczyste, choć nic się jeszcze nie generowało.
 */
export function ringArcs({ C, total, done, processing, failed, productsDone, productsTotal, countsUnit = 'product' }) {
  const pt = Math.max(Number(productsTotal) || 0, 1);
  const doneArc = Math.min((Number(productsDone) || 0) / pt, 1) * C;
  const perProduct = countsUnit === 'product';
  const procUnits = perProduct ? Math.max(0, Number(processing) || 0) : 0;
  const processingArc = Math.max(0, Math.min((procUnits / pt) * C, C - doneArc));
  const bs = perProduct ? 1 : pt / Math.max(Number(total) || 0, 1);
  const failedArc = Math.max(0, Math.min(((Number(failed) || 0) * bs / pt) * C, C - doneArc - processingArc));
  return { doneArc, processingArc, failedArc };
}
